import { useFormContext } from "react-hook-form";

import { useTranslation } from "@flare/i18n";

import { i18n } from "@/services/locale/i18n";

import { ActionButton } from "../components/action-button";
import { ActionsContainer } from "../components/actions-container";
import { BackButton } from "../components/back-button";
import { Footer } from "../components/footer";
import { PrivacyDisclaimer } from "../components/privacy-disclaimer";
import { StepContainer } from "../components/step-container";
import { StepContent } from "../components/step-content";
import { StepFeedback } from "../components/step-feedback";
import { StepTitle } from "../components/step-title";
import { funnelInputsNameMap } from "../config";

import { ChoiceBoxContent } from "./components/choice-box-content";
import type {
  ChoiceObject,
  ChoicesMap,
  PracticeList,
  StepExperiments,
  StepProps,
  SubPracticeList,
} from "./step.types";

export default function ServiceType({ answers, experiments }: StepProps) {
  const { t } = useTranslation();
  const { practice, subPractice } = answers;
  const { abTestImmigrationEligibility } = experiments;

  const isFianceRelationshipStatus =
    abTestImmigrationEligibility &&
    practice === "immigration" &&
    subPractice === "fiance-visa";

  const inputName = isFianceRelationshipStatus
    ? funnelInputsNameMap.fianceRelationshipStatus
    : funnelInputsNameMap.serviceType;

  const contentTitle = getStepTitle({
    practice,
    subPractice,
    experiments,
  });
  const choices = getStepChoices({
    practice,
    subPractice,
    experiments,
  });

  const { watch } = useFormContext();
  const serviceType = watch(inputName);

  const feedback = serviceType
    ? choices.find(({ value }) => value === serviceType)?.feedback
    : "";

  return (
    <>
      <StepContainer>
        <StepContent>
          <BackButton />
          <StepTitle>{contentTitle}</StepTitle>
          <ChoiceBoxContent name={inputName} choices={choices} required />
          <StepFeedback>{feedback}</StepFeedback>
        </StepContent>
        <ActionsContainer>
          <PrivacyDisclaimer />
          <ActionButton>{t("FUNNEL_ACTION_BUTTON_NEXT")}</ActionButton>
        </ActionsContainer>
      </StepContainer>
      <Footer />
    </>
  );
}

function getStepTitle({
  practice,
  subPractice,
  experiments,
}: {
  experiments: StepExperiments;
  practice: PracticeList | undefined;
  subPractice: SubPracticeList | undefined;
}) {
  const { abTestDivorceFlowEnabled, abTestImmigrationEligibility } =
    experiments;

  const defaultTitle = i18n.t("FUNNEL_SERVICE_TYPE_DEFAULT_TITLE");

  if (typeof practice === "undefined" || typeof subPractice === "undefined") {
    return defaultTitle;
  }

  const subPracticeToReadableName: Partial<Record<SubPracticeList, string>> = {
    divorce: i18n.t("DIVORCE"),
    "child-custody": i18n.t("CHILD_CUSTODY"),
    "child-support": i18n.t("CHILD_SUPPORT"),
  };

  const readableSubPractice = subPracticeToReadableName[subPractice] || "";

  const dynamicTitle = i18n.t("FUNNEL_SERVICE_TYPE_DYNAMIC_TITLE", {
    subPractice: readableSubPractice,
  });

  if (practice === "family") {
    if (abTestDivorceFlowEnabled && subPractice === "divorce") {
      return dynamicTitle;
    }
  }

  if (practice === "immigration") {
    const titles = {
      "green-card": i18n.t("FUNNEL_SERVICE_TYPE_GREEN_CARD_TITLE_NEW"),
      citizenship: i18n.t("FUNNEL_SERVICE_TYPE_CITIZENSHIP_TITLE"),
      "fiance-visa": abTestImmigrationEligibility
        ? i18n.t("FUNNEL_SERVICE_TYPE_FIANCE_VISA_TITLE_NEW")
        : i18n.t("FUNNEL_SERVICE_TYPE_FIANCE_VISA_TITLE_OLD"),
      "other-visa": i18n.t("FUNNEL_SERVICE_TYPE_OTHER_VISA_TITLE"),
    };

    return titles[subPractice as keyof typeof titles] || defaultTitle;
  }

  return defaultTitle;
}

function getStepChoices({
  practice,
  subPractice,
  experiments,
}: {
  experiments: StepExperiments;
  practice: PracticeList | undefined;
  subPractice: SubPracticeList | undefined;
}): ChoiceObject[] {
  if (!practice || !subPractice) {
    return [];
  }

  const { abTestDivorceFlowEnabled } = experiments;

  const familyChoices: ChoicesMap["family"] = {
    divorce: abTestDivorceFlowEnabled
      ? [
          {
            label: i18n.t("FUNNEL_SERVICE_TYPE_DIVORCE_CHOICE_1"),
            value: "Response to a divorce petition",
            feedback: i18n.t(
              "FUNNEL_SERVICE_TYPE_DIVORCE_CHOICE_RESPOND_FEEDBACK",
            ),
          },
          {
            label: i18n.t("FUNNEL_SERVICE_TYPE_DIVORCE_CHOICE_2"),
            value: "Initiate a divorce",
            feedback: i18n.t(
              "FUNNEL_SERVICE_TYPE_DIVORCE_CHOICE_INITIATE_FEEDBACK",
            ),
          },
          {
            label: i18n.t("FUNNEL_SERVICE_TYPE_DIVORCE_CHOICE_3"),
            value: "Resume an existing divorce case",
            feedback: i18n.t(
              "FUNNEL_SERVICE_TYPE_DIVORCE_CHOICE_WITH_CHILD_CUSTODY_FEEDBACK",
            ),
          },
          {
            label: i18n.t("FUNNEL_SERVICE_TYPE_DIVORCE_CHOICE_4"),
            value: "Something else",
            feedback: i18n.t(
              "FUNNEL_SERVICE_TYPE_DIVORCE_CHOICE_SOMETHING_ELSE_FEEDBACK",
            ),
          },
        ]
      : [
          {
            label: i18n.t("FUNNEL_SERVICE_TYPE_DIVORCE_CHOICE_RESPOND"),
            value: "I want to respond to a divorce petition",
            feedback: i18n.t(
              "FUNNEL_SERVICE_TYPE_DIVORCE_CHOICE_RESPOND_FEEDBACK",
            ),
          },
          {
            label: i18n.t("FUNNEL_SERVICE_TYPE_DIVORCE_CHOICE_INITIATE"),
            value: "I'm looking to initiate a divorce",
            feedback: i18n.t(
              "FUNNEL_SERVICE_TYPE_DIVORCE_CHOICE_INITIATE_FEEDBACK",
            ),
          },
          {
            label: i18n.t(
              "FUNNEL_SERVICE_TYPE_DIVORCE_CHOICE_WITH_CHILD_CUSTODY",
            ),
            value: "I want a divorce involving child custody",
            feedback: i18n.t(
              "FUNNEL_SERVICE_TYPE_DIVORCE_CHOICE_WITH_CHILD_CUSTODY_FEEDBACK",
            ),
          },
          {
            label: i18n.t("FUNNEL_SERVICE_TYPE_DIVORCE_CHOICE_SOMETHING_ELSE"),
            value: "Something else",
            feedback: i18n.t(
              "FUNNEL_SERVICE_TYPE_DIVORCE_CHOICE_SOMETHING_ELSE_FEEDBACK",
            ),
          },
        ],
    "child-custody": [
      {
        label: i18n.t("FUNNEL_SERVICE_TYPE_CHILD_CUSTODY_CHOICE_SOLE_CUSTODY"),
        value: "I want to file for sole custody",
      },
      {
        label: i18n.t(
          "FUNNEL_SERVICE_TYPE_CHILD_CUSTODY_CHOICE_SHARED_CUSTODY",
        ),
        value: "I want shared custody with my partner",
      },
      {
        label: i18n.t(
          "FUNNEL_SERVICE_TYPE_CHILD_CUSTODY_CHOICE_CHANGE_COURT_ORDER",
        ),
        value: "I want to change a custody court order",
      },
      {
        label: i18n.t(
          "FUNNEL_SERVICE_TYPE_CHILD_CUSTODY_CHOICE_SOMETHING_ELSE",
        ),
        value: "Something else",
      },
    ],
    "child-support": [
      {
        label: i18n.t("FUNNEL_SERVICE_TYPE_CHILD_SUPPORT_CHOICE_INITIATE"),
        value: "I want to file for child support",
      },
      {
        label: i18n.t("FUNNEL_SERVICE_TYPE_CHILD_SUPPORT_CHOICE_OVERDUE"),
        value: "I need assistance with overdue child support",
      },
      {
        label: i18n.t("FUNNEL_SERVICE_TYPE_CHILD_SUPPORT_CHOICE_UPDATE_TERMS"),
        value: "I'd like to change my current child support terms",
      },
      {
        label: i18n.t(
          "FUNNEL_SERVICE_TYPE_CHILD_SUPPORT_CHOICE_SOMETHING_ELSE",
        ),
        value: "Something else",
      },
    ],
    alimony: [
      {
        label: i18n.t("FUNNEL_SERVICE_TYPE_ALIMONY_CHOICE_REQUEST"),
        value: "I want to request alimony or spousal support",
      },
      {
        label: i18n.t("FUNNEL_SERVICE_TYPE_ALIMONY_CHOICE_OVERDUE_PAYMENTS"),
        value: "I need help with overdue alimony payments",
      },
      {
        label: i18n.t("FUNNEL_SERVICE_TYPE_ALIMONY_CHOICE_UPDATE_TERMS"),
        value: "I want to update my current alimony terms",
      },
      {
        label: i18n.t("FUNNEL_SERVICE_TYPE_ALIMONY_CHOICE_END_AGREEMENT"),
        value: "I want to end my alimony agreement",
      },
      {
        label: i18n.t("FUNNEL_SERVICE_TYPE_ALIMONY_CHOICE_SOMETHING_ELSE"),
        value: "Something else",
      },
    ],
    else: [
      {
        label: i18n.t("FUNNEL_SERVICE_TYPE_FAMILY_ELSE_CHOICE_CONFLICT"),
        value: "Personal or business conflict",
      },
      {
        label: i18n.t("FUNNEL_SERVICE_TYPE_FAMILY_ELSE_CHOICE_CRIMINAL"),
        value: "Criminal law matters",
      },
      {
        label: i18n.t(
          "FUNNEL_SERVICE_TYPE_FAMILY_ELSE_CHOICE_CHILD_PROTECTIVE_SERVICES",
        ),
        value: "Child Protective Services involvement",
      },
      {
        label: i18n.t(
          "FUNNEL_SERVICE_TYPE_FAMILY_ELSE_CHOICE_LEGAL_REPRESENTATION",
        ),
        value: "Changing legal representation",
      },
      {
        label: i18n.t("FUNNEL_SERVICE_TYPE_FAMILY_ELSE_CHOICE_NONE"),
        value: "None of the above",
      },
    ],
  };

  const immigrationChoices: ChoicesMap["immigration"] = {
    "green-card": [
      {
        label: i18n.t("FUNNEL_SERVICE_TYPE_GREEN_CARD_CHOICE_APPLY"),
        value: "New green card application",
      },
      {
        label: i18n.t("FUNNEL_SERVICE_TYPE_GREEN_CARD_CHOICE_2_YEAR_EXPIRING"),
        value: "Expiring 2-year green card",
      },
      {
        label: i18n.t("FUNNEL_SERVICE_TYPE_GREEN_CARD_CHOICE_10_YEAR_EXPIRING"),
        value: "Expiring 10-year green card",
      },
      {
        label: i18n.t("FUNNEL_SERVICE_TYPE_GREEN_CARD_CHOICE_SOMETHING_ELSE"),
        value: "Something else",
      },
    ],
    citizenship: [
      {
        label: i18n.t(
          "FUNNEL_SERVICE_TYPE_CITIZENSHIP_CHOICE_GREEN_CARD_HOLDER",
        ),
        value: "I'm a green card holder",
      },
      {
        label: i18n.t(
          "FUNNEL_SERVICE_TYPE_CITIZENSHIP_CHOICE_ANCESTOR_CITIZEN",
        ),
        value: "My parent or grandparent is a U.S. citizen",
      },
      {
        label: i18n.t("FUNNEL_SERVICE_TYPE_CITIZENSHIP_CHOICE_SOMETHING_ELSE"),
        value: "Something else",
      },
    ],
    "fiance-visa": getFianceVisaChoices(experiments),
    "other-visa": [
      {
        label: i18n.t("FUNNEL_SERVICE_TYPE_OTHER_VISA_CHOICE_EMPLOYMENT"),
        value: "Employment visa",
      },
      {
        label: i18n.t("FUNNEL_SERVICE_TYPE_OTHER_VISA_CHOICE_INVESTOR"),
        value: "Investor visa",
      },
      {
        label: i18n.t("FUNNEL_SERVICE_TYPE_OTHER_VISA_CHOICE_STUDENT"),
        value: "Student visa",
      },
      {
        label: i18n.t("FUNNEL_SERVICE_TYPE_OTHER_VISA_CHOICE_TOURIST"),
        value: "Tourist visa",
      },
      {
        label: i18n.t("FUNNEL_SERVICE_TYPE_OTHER_VISA_CHOICE_ELSE"),
        value: "else",
      },
    ],
    else: [
      {
        label: i18n.t(
          "FUNNEL_SERVICE_TYPE_IMMIGRATION_ELSE_CHOICE_ADDITIONAL_INFO",
        ),
        value: "Additional information",
      },
      {
        label: i18n.t(
          "FUNNEL_SERVICE_TYPE_IMMIGRATION_ELSE_CHOICE_APPEAL_DENIAL",
        ),
        value: "Appeal a denial",
      },
      {
        label: i18n.t(
          "FUNNEL_SERVICE_TYPE_IMMIGRATION_ELSE_CHOICE_SOMETHING_ELSE",
        ),
        value: "Something else",
      },
    ],
  };

  const choicesMap: ChoicesMap = {
    family: familyChoices,
    immigration: immigrationChoices,
  };

  return choicesMap[practice][subPractice] || [];
}

function getFianceVisaChoices(experiments: StepExperiments): ChoiceObject[] {
  if (!experiments.abTestImmigrationEligibility) {
    return [
      {
        label: i18n.t("FUNNEL_SERVICE_TYPE_FIANCE_VISA_CHOICE_YES"),
        value: "Yes",
      },
      {
        label: i18n.t("FUNNEL_SERVICE_TYPE_FIANCE_VISA_CHOICE_NO"),
        value: "No",
      },
    ];
  }

  if (experiments.isAdditionalFianceVisaStepEnabled) {
    return [
      {
        label: i18n.t(
          "FUNNEL_SERVICE_TYPE_FIANCE_VISA_CHOICE_ENGAGED_ADDITIONAL_STEPS_FLOW",
        ),
        value: "We're engaged",
      },
      {
        label: i18n.t(
          "FUNNEL_SERVICE_TYPE_FIANCE_VISA_CHOICE_RELATIONSHIP_ADDITIONAL_STEPS_FLOW",
        ),
        value: "We're married",
      },
      {
        label: i18n.t("FUNNEL_SERVICE_TYPE_FIANCE_VISA_CHOICE_SOMETHING_ELSE"),
        value: "Something else",
      },
    ];
  }

  return [
    {
      label: i18n.t("FUNNEL_SERVICE_TYPE_FIANCE_VISA_CHOICE_ENGAGED"),
      value: "I’m engaged to a U.S. citizen",
    },
    {
      label: i18n.t("FUNNEL_SERVICE_TYPE_FIANCE_VISA_CHOICE_RELATIONSHIP"),
      value: "I’m in a relationship with a U.S. citizen",
    },
    {
      label: i18n.t("FUNNEL_SERVICE_TYPE_FIANCE_VISA_CHOICE_SOMETHING_ELSE"),
      value: "Something else",
    },
  ];
}
